/* eslint-disable react/prop-types */
import React, { useRef, useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import proj4 from 'proj4';
import L, { map } from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { Box, Menu, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { MapContainer, useMap, TileLayer } from 'react-leaflet';

import Loader from 'components/Common/Loader';
import Layers from 'components/MapComponent/Layers/Layers';
import ElevationChart from 'components/MapComponent/ElevationChart';
import MarkerTable from 'components/MapComponent/Marker/MarkerTable';
import TopFilters from 'components/MapComponent/TopFilters/TopFilters';
import CellDetails from 'components/MapComponent/CellDetails/CellDetails';
import TroubleTicketsTable from 'components/MapComponent/TroubleTickets/TroubleTicketsTable';
import SearchSuggestions from 'components/MapComponent/TopFilters/SearchFilter/SearchSuggestions';

import {
  registerFilter,
  changeFilterValues,
  getFiltersByDashboardId,
} from 'features/filtersState';
import {
  splitLine,
  createEdge,
  extractCells,
  groupMarkersFromData,
  createFeatureCollection,
  checkMarkersInsidePolygon,
  calculateOrientationShiftedPosition,
  cellMarkersReprPolySizesPerZoom,
  bingLayerOptions,
} from 'utils/Map/mapUtils';
import {
  setBandsList,
  setMapPosition,
  setClickedCell,
  setGranularityDates,
  setHighlightedCriterias,
  setGlobalLayersAsync,
  setGlobalLayersLoaded,
  updateLayerByLayerId,
} from 'features/advancedMapState';
import { DashboardLayerType } from 'types/types';
import { generateQueryParams } from 'utils/Utils';
import { showAlert } from 'features/applicationState';
import {
  cleanUpMap,
  calculateMidpoint,
} from '../components/MapComponent/Helpers/mapHelpers';
import { NiKendoContext } from 'context/NiKendoContext';
import { setMapPopupState } from 'features/applicationState';
import {
  fetchMarkers,
  checkIfCacheExists,
  removeItemIfExists,
  fetchElementDetailsLayerData as fetchElementDetailsLayerData,
} from 'utils/Map/mapService';
import { 
  toLatLng,
  getOriginLabel,
  getFeatureColor,
  GEOJSON_HIDDEN_PROPS,
  getDestinationLabel,
  getGeometryEndpoints,
  DEFAULT_GEOJSON_STYLE
} from 'components/MapComponent/Layers/GeoJsonLayerHelper';
import DataProviderService from 'services/DataProviderService';
import { debounceFn } from 'components/MapComponent/Helpers/generalHelpers';
import { useGetTileLayersUrls } from 'components/MapComponent/Helpers/hooks';
import { dashboards, setDashboardDescriptor } from 'features/dashboardsState';
import { getBrowserDateShiftedToUserTZ } from 'components/Common/GranularityDateRangePicker/helper';
import LeafletSiteMarkerCircle from 'components/MapComponent/LeafletExtensionClasses/LeafletSiteMarkerCircle';
import LeafletAzimuthMarkerLine from 'components/MapComponent/LeafletExtensionClasses/LeafletAzimuthMarkerLine';
import LeafletCellMarkerReprPoly from 'components/MapComponent/LeafletExtensionClasses/LeafletCellMarkerReprPoly';
import LeafletExtensionGeoJSONGridLayer from 'components/MapComponent/LeafletExtensionClasses/LeafletExtensionGeoJSONGridLayer';
import FeaturesRenderingLayer from 'components/MapComponent/LeafletExtensionClasses/FeaturesRenderingLayer';
import SQMRenderingLayer from 'components/MapComponent/LeafletExtensionClasses/SQMRenderingLayer';

import 'leaflet-draw';
import 'leaflet-editable';
import 'tilelayer-canvas';
import 'leaflet-bing-layer';
import 'leaflet.markercluster';
import 'leaflet-canvas-marker';
import 'leaflet-better-filelayer';
import 'assets/map/js/leaflet.sector';
import '@geoman-io/leaflet-geoman-free';

import 'assets/styles/map.scss';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

let kpiFiltered = {
  selectedValues: [],
  markers: [],
};
let updateLayers = null;
let visibleMarkers = [];
let selectedRegionId = '';
let markersListFromServer = [];
let cellIdentifierKey = 'None';
let siteIdentifierKey = 'None';
let markersListAfterCellFilters = [];

const MapComponent = () => {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
  proj4.defs(
    'EPSG:2154',
    '+proj=lcc +lat_1=44 +lat_2=49 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );

  const dashboardId = 'ni-ran-advanced-map-dashboard';

  const labelsZoomThreshold = 16;
  const triangleZoomThreshold = 11;

  const MOVE = 'Move';
  const ZOOM = 'Zoom';

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const currentUrl = window.location.href;
  const hashIndex = currentUrl.indexOf('#');
  const hash = currentUrl.substring(hashIndex);
  const queryString = hash.includes('?') ? hash.split('?')[1] : '';

  const filtersState = useSelector((state) =>
    getFiltersByDashboardId(state, dashboardId)
  );
  const mapPopupState = useSelector(
    (state) => state.applicationState.mapPopupState
  );
  const {
    zoom,
    center,
    bandsList,
    clickedCell,
    selectedBands,
    granularityDates,
    highlightedCriterias,
    globalLayers: reduxGlobalLayers,
    mapStyles: mapStyles,
  } = useSelector((state) => state.advancedMap);
  const dashboardsState = useSelector((state) => dashboards(state)) || [];

  const mapRef = useRef(null);
  const mapBoundsRef = useRef();
  const typeLayersRef = useRef([]);
  const mainLayerRef = useRef(null);
  const tileLayerRef = useRef(null);
  const topFiltersRef = useRef(null);
  const circleLayerRef = useRef(null);
  const markerTableRef = useRef(null);
  const kpiNRPropertyRef = useRef('');
  const geoJsonLayerRef = useRef(null);
  const kpiLTEPropertyRef = useRef('');
  const kqiFiltersSelected = useRef([]);
  const previewButtonRef = useRef(null);
  const highlightLayerRef = useRef(null);
  const searchSuggestionsRef = useRef(null);
  const geoJsonLayersLoaded = useRef(false);
  const highlightCellLayerRef = useRef(null);
  const clickedCellRef = useRef(clickedCell);
  const regionsFilterRef = useRef('UNKNOWN');
  const elementDetailsLayerRef = useRef(null);
  const troubleTicketsTableRef = useRef(null);
  const layersLoadedFromCacheRef = useRef([]);
  const globalLayersControlRef = useRef(null);
  const mapPopupStateRef = useRef(mapPopupState);
  const selectedMapStyle = useRef(mapStyles.selected);
  const highlightedCriteriasRef = useRef(highlightedCriterias);
  const globalLayersLeafletLayerGroupRef = useRef(null);
  const customGeoJsonLayersRef = useRef({});
  const highlightedGeoJsonLayerRef = useRef(null);
  const geoJsonFeatureLabelGroupRef = useRef(null);
  const granularityDatesRef = useRef(granularityDates);

  const [customGeoJsonLayersData, setCustomGeoJsonLayersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({});
  const [drawnedLine, setDrawnedLine] = useState(null);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [cellElevationChart, setCellElevationChart] = useState(false);
  const [lineContextMenuOpened, setLineContextMenuOpened] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ top: 0, left: 0 });
  const [activeElementDetailsLayers, setActiveElementDetailsLayers] = useState(
    {}
  );

  const maxNumberOfLayersSelection = 3;

  const TILE_LAYER_URLS = useGetTileLayersUrls();

  const { niKendoModule } = useContext(NiKendoContext);

  const handleCloseMarkersModal = () => {
    setSelectedMarkers([]);
    markerTableRef.current.mapPopupRef.current.handleOpenModal(false);
  };

  const handleTroubleTicketsPopup = () => {
    troubleTicketsTableRef.current.mapPopupRef.current.handleOpenModal(false);
    previewButtonRef.current.handlePreviewSelected(false);
  };

  const handleCloseCellDetails = () => {
    if (highlightCellLayerRef.current) {
      highlightCellLayerRef.current.clearLayers();
    }
    if (elementDetailsLayerRef.current) {
      setActiveElementDetailsLayers({});
      elementDetailsLayerRef.current.clearLayers();
    }
    troubleTicketsTableRef.current?.mapPopupRef.current.handleOpenModal(false);
    dispatch(setClickedCell(null));
  };

  const findByCell = (value, filterKey, data) => {
    const foundElem = data.find((el) => el[filterKey] === value);
    if (foundElem) {
      return { item: foundElem };
    }
    return null; // Return null if no match is found
  };

  const findBySite = (value, filterKey, data) => {
    for (let i = 0; i < data.length; i++) {
      const siteIndex = data[i].sites.findIndex(
        (site) => site[filterKey] === value
      );
      if (siteIndex !== -1) {
        return { item: data[i], siteIndex: siteIndex, dataIndex: i };
      }
    }
    return null; // Return null if no match is found
  };
  const goToUrlItem = (arrayMarkers) => {
    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      if (
        searchParams.get('elementLevel') &&
        searchParams.get('elementValue')
      ) {
        const elementLevel = searchParams.get('elementLevel');
        const elementValue = searchParams.get('elementValue');
        const isSite = elementLevel === 'SITE';
        const filterKey = isSite ? 'SITE_NATURAL_KEY' : 'key';
        const result = isSite
          ? findBySite(elementValue, filterKey, markersListFromServer)
          : findByCell(elementValue, filterKey, arrayMarkers);
        if (result) {
          const coordinates = [
            result.item.location.lat,
            result.item.location.lng,
          ];
          let updatedHighlightCriterias = [
            ...highlightedCriterias,
            { id: 'id', value: result.item['id'] },
          ];
          dispatch(setHighlightedCriterias(updatedHighlightCriterias));
          flyToCoordinates(coordinates, 18);
          isSite
            ? handleSiteClicked(null, result.item, result.siteIndex)
            : handleCellClicked(null, result.item, elementLevel.toLowerCase());
        }
        let enabledLayer = searchParams.get('enabledLayer');
        if(enabledLayer){
          const layerToEnable = reduxGlobalLayers.find(
            (el) => el.LayerId== enabledLayer
          );
          if(layerToEnable){
            dispatch(
              updateLayerByLayerId({
                LayerId: enabledLayer,
                diff: {
                  selected: true
                }
              })
            );
            handleGlobalLayerDataEvent(layerToEnable);
          }
        }

      }
    }
  };

  const loadMarkers = async (filters, regionData = true) => {
    setLoading(true);
    const markersData = await fetchMarkers(filters);
    let arrayMarkers = Object.keys(markersData).map((key) => ({
      ...markersData[key],
      ...markersData[key].contextInfo,
      key: key, // Add the original key to the object if needed
    }));
    regionsFilterRef.current = filters?.datafilters[0]?.values[0]?.id;
    if (regionData) {
      arrayMarkers = arrayMarkers.sort((a, b) => a.contextInfo["CELL BAND Name"].localeCompare(b.contextInfo["CELL BAND Name"]));
      markersListFromServer = groupMarkersFromData(bandsList, arrayMarkers);

      applyCellFilters();

      goToUrlItem(arrayMarkers);
      // if we have the KPI layer enabled, then launch the re-request
      const isKPILayerSelected = reduxGlobalLayers.find(
        (el) => el.LayerName == 'Technology KPI' && el.selected == true
      );
      if (isKPILayerSelected) {
        fetchTechnologiesKPIsData().then(() => {
          setLoading(false);
        });
      } else {
        setLoading(false);
      }

      // inform the global layers that the base markers have changed
      // maybe some of those layers requires a re-rendering after that
      globalLayersLeafletLayerGroupRef.current?.eachLayer(function (layer) {
        if (layer['baseMarkersLoadedOrRefreshed']) {
          layer.baseMarkersLoadedOrRefreshed(markersListFromServer);
        }
      });
    } else {
      return { arrayMarkers };
    }
  };

  const loadDashboardDescriptor = async (dashboardId) => {
    try {
      const dashboard = dashboardsState.find(
        (d) => d.dashboardId === dashboardId
      );
      if (dashboard) {
        applyFiltersFromUrl();
        setFiltersLoaded(true);
        return dashboard.descriptor;
      }
      const dpService = new DataProviderService();
      const descriptorItem = await dpService.getDashboardDescriptor(
        dashboardId
      );
      setLoading(true);
      const mapFilters = await dpService.getAdvancedMapDataFilters({
        dashboardId: dashboardId,
      });
      setLoading(false);
      mapFilters.forEach((filter) => {
        filter.sourceType = 'localSource';
        switch (filter.filterId) {
          case 'KPI_LTE':
          case 'KPI_NR':
            filter.selectionType = 'singleSelection';
            filter.category = 'mapKpiFilter';
            filter.selectedValues = [filter.values[0]];
            break;
          case 'KPI_LTE2LTE':
          case 'KPI_LTE2NR':
          case 'KPI_NR2NR':
            filter.selectionType = 'singleSelection';
            filter.category = 'mapNeighborKpiFilter';
            filter.selectedValues =
              filter.values.length > 0 ? [filter.values[0]] : [];
            break;
          case 'KQI_LTE':
          case 'KQI_NR':
            filter.selectionType = 'multipleSelection';
            filter.category = 'mapKqiFilter';
            filter.selectedValues =
              filter.values.length > 0 ? [filter.values[0]] : [];
            filter.maxSelectedValues = 5;
            filter.maxSelectedValuesAlertMessage =
              'Max numbers of fields reached';
            break;
          case 'CELL_IDENTIFIER':
            filter.selectionType = 'singleSelection';
            filter.category = 'cellIdentifier';
            filter.selectedValues = [filter.values[0]];
            break;
          case 'SITE_IDENTIFIER':
            filter.selectionType = 'singleSelection';
            filter.category = 'cellIdentifier';
            filter.selectedValues = [filter.values[0]];
            break;
          case 'CELL_BAND_NAME':
            filter.selectionType = 'multipleSelection';
            filter.category = filter.filterId;
            filter.selectedValues = filter.values;
            dispatch(setBandsList(filter.values));
            break;
          case 'REGION_NAME':
            filter.selectionType = 'singleSelection';
            filter.category = 'regionName';
            if (mapPopupState.region) {
              filter.selectedValues = [
                filter.values.find(
                  (value) => value.id === mapPopupState.region
                ),
              ];
            } else {
              filter.selectedValues = [filter.values[0]];
            }
            break;
          default:
            filter.selectionType = 'multipleSelection';
            filter.category = 'localFilter';
            filter.selectedValues = filter.values;
            break;
        }
        dispatch(registerFilter({ dashboardId, filter: filter, loaded: true }));
      });
      applyFiltersFromUrl();
      const descriptor = {
        ...descriptorItem,
        filters: mapFilters,
      };
      dispatch(
        setDashboardDescriptor({
          dashboardId,
          descriptor,
        })
      );
      await dispatch(
        setGlobalLayersAsync([
          ...descriptor.settings.technologyLayerConfig,
          ...descriptor.settings.globalLayersConfig,
        ])
      )
      dispatch(
        setGlobalLayersLoaded(true)
      );
      setFiltersLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const showModal = () =>
    markerTableRef.current.mapPopupRef.current.handleOpenModal(true);

  const handleSiteClicked = (e, markerInfo = null, siteIndex = 0, flyToIt = false) => {
    let siteMarkerInfo = markerInfo || e.target.siteMarkerInfo;
    if (mapPopupStateRef.current.isVisible) return;

    // if the clicked SITE is the same as the one being displayed then dont do anything
    if (
      clickedCellRef.current?.type === 'site' &&
      clickedCellRef.current?.location.lat == siteMarkerInfo.location.lat &&
      clickedCellRef.current?.location.lng == siteMarkerInfo.location.lng
    ) {
      return;
    }
    if (clickedCellRef.current) {
      handleCloseCellDetails();
    }

    // flag to use when searching KPI values
    let filteredKpi =
      kpiFiltered.selectedValues.length > 0 && kpiFiltered.markers.length > 0;

    // find one cell per each SITE of the clicked lat/long
    const oneCellPerSite = {}; // dynamic obejct with the KEY being site_natural_key
    siteMarkerInfo.orientations.forEach((orientationMarkerInfo) => {
      orientationMarkerInfo.markers.forEach((cellMarkerInfo) => {
        // first time found the cell asociated for a SITE
        let siteKey =
          cellMarkerInfo['TECHNOLOGY'] + cellMarkerInfo['SITE_NATURAL_KEY'];
        if (oneCellPerSite[siteKey]) {
          oneCellPerSite[siteKey] = {
            ...oneCellPerSite[siteKey],
            ...cellMarkerInfo.contextInfo,
          };
        } else {
          oneCellPerSite[siteKey] = {
            ...cellMarkerInfo.contextInfo,
            location: cellMarkerInfo.location,
          };
        }
        // now search its KPI values
        if (filteredKpi) {
          let filteredMarkers = kpiFiltered.markers.find(
            (m) =>
              m.id === cellMarkerInfo.id &&
              Object.keys(m).some(
                (key) =>
                  kpiFiltered.selectedValues.includes(key) && m[key] !== ''
              )
          );
          if (filteredMarkers) {
            oneCellPerSite[siteKey] = {
              ...filteredMarkers.contextInfo,
              ...cellMarkerInfo.contextInfo,
              location: cellMarkerInfo.location,
            };
          }
        }
        // }
      }); // end orientation's cells iteration
    }); // end site's orientations iteration
    // now translate oneCellPerSite dynamic object into an array
    dispatch(
      setClickedCell({
        sites: oneCellPerSite,
        type: 'site',
        location: siteMarkerInfo.location,
        siteIndex: siteIndex,
      })
    );
    if (flyToIt) {
      recenterClickedCellSite(siteMarkerInfo);
    }
  };

  const handleCellClicked = (e, cellMarkerInfo, type = 'cell', flyToIt = false) => {
    if (mapPopupStateRef.current.isVisible) return;
    // if the clicked cell is the same as the one being displayed then dont do anything
    if (clickedCellRef.current?.id == cellMarkerInfo.id) {
      return;
    }
    if (clickedCellRef.current) {
      handleCloseCellDetails();
    }

    let filteredKpi =
      kpiFiltered.selectedValues.length > 0 && kpiFiltered.markers.length > 0;
    if (filteredKpi && type === 'cell') {
      let filteredMarkers = kpiFiltered.markers.find(
        (m) => m.id === cellMarkerInfo.id
      );
      if (filteredMarkers) {
        cellMarkerInfo = {
          ...cellMarkerInfo,
          contextInfo: {
            ...cellMarkerInfo.contextInfo,
            ...filteredMarkers.contextInfo,
          },
        };
      }
    }
    dispatch(setClickedCell({...cellMarkerInfo}));
    if (flyToIt) {
      recenterClickedCellSite(cellMarkerInfo);
    }
  };

  const handleGlobalLayerDataEvent = (
    layerDefinition /*: DashboardLayerConfig*/
  ) => {
    if (layersLoadedFromCacheRef.current.includes(layerDefinition.LayerId)) {
      layersLoadedFromCacheRef.current =
        layersLoadedFromCacheRef.current.filter(
          (item) => item !== layerDefinition.LayerId
        );
    }
    // Define the options to instantiate the NI layer leaflet extension
    const niLeafletLayerOptions = {
      layerDefinition: layerDefinition,
      minZoom: layerDefinition.MinZoomLevelSupported,
      reduxDispatchCallback: dispatch,
      initialFiltersState: filtersState,
      initialGlobalDateFilter: granularityDatesRef.current,
      initialBaseMarkers: markersListFromServer,
      opacity: 0.8
    };
    // Factory to determine the type of layer to create
    switch (layerDefinition.LayerType) {
      case DashboardLayerType.CssStyleGeoJsonGDBFileBased:
        new LeafletExtensionGeoJSONGridLayer(niLeafletLayerOptions).addTo(
          globalLayersLeafletLayerGroupRef.current
        );
        break;
      case DashboardLayerType.GlobalFilterBasedCustomRenderer:
        if (
          layerDefinition.LayerId === "SQM_BRANDS" ||
          layerDefinition.LayerId === "SQM_CAUSES"
        ){
          new SQMRenderingLayer(
            niLeafletLayerOptions
          ).addTo(globalLayersLeafletLayerGroupRef.current);
        }else{
          new FeaturesRenderingLayer(
            niLeafletLayerOptions
          ).addTo(globalLayersLeafletLayerGroupRef.current);
        }
        break;
      default:
        console.error('Unknown layer type: ' + layerDefinition.LayerType);
    }
  };

  const setLayerLoading = (layerDefinition, loading) => {
    dispatch(
      updateLayerByLayerId({
        LayerId: layerDefinition.LayerId,
        diff: {
          isLoading: loading
        }
      })
    );
  };

  const handleLayersDataEvents = (
    isLayerSelected /*: boolean*/,
    layerDefinition /*: DashboardLayerConfig*/,
  ) => {

    let layersToHide = [];

    if(layerDefinition.VisibilityGroup){
        layersToHide = reduxGlobalLayers.filter((el) => el.VisibilityGroup === layerDefinition.VisibilityGroup && el.selected && el.LayerId !== layerDefinition.LayerId);
    };
    if(!isLayerSelected){
      layersToHide.push(layerDefinition);
    }
    if(layersToHide.length>0){
      layersToHide.forEach((layerToHide) => {
        // Find the layer instance by matching LayerId
        removeItemIfExists(layerToHide.LayerId);
        dispatch(
          updateLayerByLayerId({
            LayerId: layerToHide.LayerId,
            diff: {
              selected: false,
            },
          })
        );
        switch (layerToHide.LayerType) {
          case DashboardLayerType.KPI:
            removeTechnologiesKPIsData();
          break;
          default:{
            let layerInstanceToRemove = null;
            globalLayersLeafletLayerGroupRef.current?.eachLayer((layer) => {
              if (layer.options?.layerDefinition?.LayerId === layerToHide.LayerId) {
                layerInstanceToRemove = layer;
              }
            });
            globalLayersLeafletLayerGroupRef.current?.removeLayer(layerInstanceToRemove);
            break;
          }
        }
      });
    };

    if(isLayerSelected){
      const reduxSelectedLayers = reduxGlobalLayers.filter((el) => el.selected);
      if(reduxSelectedLayers.length === maxNumberOfLayersSelection){
        const alertData = {
          severity: 'warning',
          title: 'Max number of layers was reached',
          message: `Max number of layers was reached`,
          open: true,
        };
        dispatch(showAlert(alertData));
        return;
      }
      dispatch(
        updateLayerByLayerId({
          LayerId: layerDefinition.LayerId,
          diff: {
            selected: true
          }
        })
      );
      switch (layerDefinition.LayerType) {
        case DashboardLayerType.KPI:
          setLayerLoading(layerDefinition, true);
          fetchTechnologiesKPIsData().then(() => {
            setLayerLoading(layerDefinition, false);
          });
        break;
        default:
          handleGlobalLayerDataEvent(
            layerDefinition
          );
          break;
      }
    }
  };

  const handleGeoJsonFeatureClick = (feature, featureLayer) => {
    // Restore the previous feature's style via the parent GeoJSON layer's resetStyle
    if (highlightedGeoJsonLayerRef.current) {
      highlightedGeoJsonLayerRef.current._parentGeoJson?.resetStyle(
        highlightedGeoJsonLayerRef.current
      );
    }

    // Remove old endpoint labels
    geoJsonFeatureLabelGroupRef.current?.remove();
    geoJsonFeatureLabelGroupRef.current = null;

    // Highlight the clicked feature — setStyle overrides Leaflet's inline SVG styles
    featureLayer.setStyle({ color: '#000000', weight: 3, opacity: 1, fillOpacity: 0.35 });
    highlightedGeoJsonLayerRef.current = featureLayer;

    // Place origin / destination labels at geometry start and end
    const { first, last } = getGeometryEndpoints(feature.geometry);
    const originLabel = getOriginLabel(feature.properties);
    const destLabel = getDestinationLabel(feature.properties);

    if ((first && originLabel) || (last && destLabel)) {
      geoJsonFeatureLabelGroupRef.current = L.layerGroup().addTo(mapRef.current);

      if (first && originLabel) {
        const ll = toLatLng(first);
        if (ll) {
          L.marker(ll, {
            icon: L.divIcon({ className: 'uploaded-geojson-label', html: originLabel, iconAnchor: [-4, 12] }),
            interactive: false,
          }).addTo(geoJsonFeatureLabelGroupRef.current);
        }
      }

      if (last && destLabel) {
        const ll = toLatLng(last);
        if (ll) {
          L.marker(ll, {
            icon: L.divIcon({ className: 'uploaded-geojson-label', html: destLabel, iconAnchor: [-4, 12] }),
            interactive: false,
          }).addTo(geoJsonFeatureLabelGroupRef.current);
        }
      }
    }
  };

  const handleUploadGeoJsonLayer = (id, name, geojsonData) => {
    const layer = L.geoJSON(geojsonData, {
      className: 'uploaded-geojson-layer',
      style: (feature) => {
        const color = getFeatureColor(feature?.properties);
        if (!color) return DEFAULT_GEOJSON_STYLE;
        return { color, fillColor: color, weight: 3, opacity: 0.9, fillOpacity: 0.5 };
      },
      pointToLayer: (feature, latlng) => {
        const color = getFeatureColor(feature?.properties) || DEFAULT_GEOJSON_STYLE.color;
        return L.circleMarker(latlng, {
          radius: 6,
          color,
          fillColor: color,
          fillOpacity: 0.7,
          className: 'uploaded-geojson-point',
        });
      },
      onEachFeature: (feature, featureLayer) => {
        if (feature.properties && Object.keys(feature.properties).length > 0) {
          const content = Object.entries(feature.properties)
            .filter(([k]) => !GEOJSON_HIDDEN_PROPS.some((prefix) => k.startsWith(prefix)))
            .map(([k, v]) => `<b>${k}:</b> ${v}`)
            .join('<br/>');
          featureLayer.bindPopup(`<div class="uploaded-geojson-popup">${content}</div>`);
        }
        featureLayer.on('click', () => handleGeoJsonFeatureClick(feature, featureLayer));
      },
    });
    // Store a back-reference so each feature layer can call resetStyle on its parent
    layer.eachLayer((fl) => { fl._parentGeoJson = layer; });
    customGeoJsonLayersRef.current[id] = layer;
    if (globalLayersLeafletLayerGroupRef.current) {
      globalLayersLeafletLayerGroupRef.current.addLayer(layer);
    }
    try {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [40, 40] });
      }
    } catch (e) {
      console.warn('Could not fit bounds for uploaded GeoJSON layer', e);
    }
    setCustomGeoJsonLayersData((prev) => [
      ...prev,
      { id, name, features: geojsonData.features || [] },
    ]);
  };

  const handleToggleCustomGeoJsonLayer = (id, visible) => {
    const layer = customGeoJsonLayersRef.current[id];
    if (!layer || !globalLayersLeafletLayerGroupRef.current) return;
    if (visible) {
      globalLayersLeafletLayerGroupRef.current.addLayer(layer);
    } else {
      globalLayersLeafletLayerGroupRef.current.removeLayer(layer);
    }
  };

  const handleRemoveCustomGeoJsonLayer = (id) => {
    const layer = customGeoJsonLayersRef.current[id];
    if (layer && globalLayersLeafletLayerGroupRef.current) {
      globalLayersLeafletLayerGroupRef.current.removeLayer(layer);
    }
    delete customGeoJsonLayersRef.current[id];
    setCustomGeoJsonLayersData((prev) => prev.filter((l) => l.id !== id));
  };

  const openContextMenuAt = (pos) => {
    setContextMenuPos({
      top: pos.y, // Set the y-coordinate
      left: pos.x, // Set the x-coordinate
    });
    setLineContextMenuOpened(true);
  };

  const showEarthElevation = () => {
    if (!drawnedLine) {
      return;
    }
    createFeatureCollection(drawnedLine.linePoints, 10, 0, true).then(function (
      data
    ) {
      setChartData({ ...data, hideTiltLine: true });
      setCellElevationChart(true);
    });
    setLineContextMenuOpened(false);
  };

  const showLineDistance = () => {
    if (!drawnedLine) {
      return;
    }
    const popupContent = `Total Distance: ${drawnedLine.totalDistance.toFixed(
      2
    )} km`;
    drawnedLine.layer.bindPopup(popupContent).openPopup();
    setLineContextMenuOpened(false);
  };

  const siteStreetView = () => {
    if (!clickedCellRef.current) {
      return;
    }
    let streetViewLink = `https://earth.google.com/web/@${clickedCell.location.lat},${clickedCell.location.lng},5000a,${clickedCellRef.current.orientation}h,${clickedCellRef.current['MECHANICAL TILT']}t,0r`;
    window.open(streetViewLink, '_blank');
  };

  const cmDump = (cell, elementLevel) => {
    const clickedElement = {
      level: elementLevel,
      vendor: cell['VENDOR'].toLowerCase(),
      element: cell['SITE_NATURAL_KEY'],
      site: cell['SITE NAME'],
      technology: cell[`TECHNOLOGY`].toLowerCase(),
    };

    let cmDumpDate = new Date().toISOString().split('T')[0];
    if (
      granularityDatesRef.current &&
      granularityDatesRef.current.values &&
      granularityDatesRef.current.values.length
    ) {
      if (!granularityDatesRef.current.values[0].id.includes('|')) {
        cmDumpDate = niKendoModule.application.utils.date.parseDateInterval(
          granularityDatesRef.current.values[0].id
        )[1];
      } else {
        cmDumpDate = granularityDatesRef.current.values[0].id.split('|')[1];
      }
    }

    const level = clickedElement.level;
    const vendor = clickedElement.vendor;
    const date = new Date(cmDumpDate);
    const element = clickedElement.element;
    const site = clickedElement.site;
    const technology = clickedElement.technology;
    date.setHours(0, 0, 0, 0);

    const dateRange = [
      {
        id: `${niKendoModule.application.utils.date.toDateTimeFormatFilter(
          date
        )}|${niKendoModule.application.utils.date.toDateTimeFormatFilter(
          date
        )}`,
      },
      { id: 'daily' },
    ];

    let viewId = `ni-ran-cm-dump-${vendor}-${technology}`;
    if (niKendoModule.application.views[viewId]) {
      niKendoModule.application.views[viewId].setParentDashboardSyncData({
        filters: {
          globalDateRangeFilter: dateRange,
          globalSitesFilter: [{ id: element, name: site }],
          globalCellSiteFilter: [
            {
              id: level,
              name: niKendoModule.application.utils.capitalize(level),
            },
          ],
        },
      });
      navigate(
        `/view/${viewId}?${generateQueryParams(
          niKendoModule.application.views[viewId].properties
        )}`
      );
    }
  };

  const detailsCmDump = (cell) => {
    if (!clickedCell) {
      return;
    }
    cmDump({ ...clickedCell, ...cell }, cell.type ? cell.type : 'cell');
  };

  const performanceDetailsCell = (cell) => {
    const clickedElement = {
      element: cell.id,
      technology: cell['TECHNOLOGY'],
    };
    performanceDetails(clickedElement);
  };

  const performanceDetailsSite = (cell) => {
    const clickedElement = {
      element: cell['SITE_NATURAL_KEY'],
      technology: cell['TECHNOLOGY'],
    };
    performanceDetails(clickedElement);
  };

  const performanceDetails = (clickedElement) => {
    const element = clickedElement.element;
    var technology =
      clickedElement.technology.toUpperCase() == 'LTE' ? '4g' : '5g';

    let viewId = `ni-ran-${technology}-dashboard`;
    const filterObject = filtersState.find(
      (filter) =>
        filter.filterId === `KPI_${clickedElement.technology.toUpperCase()}`
    );
    if (niKendoModule.application.views[viewId]) {
      niKendoModule.application.views[viewId].setParentDashboardSyncData({
        globalDateFilterControl: granularityDatesRef.current.values,
        globalRegionFilterControl: [{ id: selectedRegionId }],
        globalHierarchyFilterControl: {
          hierarchyType: `${clickedElement.technology.toUpperCase()}_NETWORK`,
          values: [{ id: element }],
        },
        globalConditionKpiFilterControl: filterObject.selectedValues,
      });
      navigate(`/view/${viewId}`);
    }
  };
  const displayHeightGraph = async () => {
    if (highlightLayerRef.current) {
      highlightLayerRef.current.clearLayers();
    }
    if (!clickedCell) {
      return;
    }
    const createLine = createEdge(
      clickedCell.orientation,
      clickedCell.location.lat,
      clickedCell.location.lng,
      10,
      true,
      3
    );
    setCellElevationChart(true);
    const linesLatLng = createLine.map((line) => {
      return { lat: line[0], lon: line[1] };
    });
    const { linesData, earthElevationData, distanceSteps, points } =
      await createFeatureCollection(
        linesLatLng,
        parseInt(clickedCell['CELL ANTENNA HEIGHT']),
        parseInt(clickedCell['MECHANICAL TILT'] || 0)
      );
    setChartData({ linesData, earthElevationData, distanceSteps, points });
    const displayGroup = new L.LayerGroup();
    L.polyline(linesLatLng).addTo(displayGroup);

    highlightLayerRef.current.addLayer(displayGroup);
  };

  const recenterClickedCellSite = (selectedItem) => {
    if (!selectedItem || !selectedItem.location) {
      return;
    }
    flyToCoordinates(
      [selectedItem.location.lat, selectedItem.location.lng],
      18);
    /*mapRef.current.setView(
      [clickedCell.location.lat, clickedCell.location.lng],
      18
    );*/
  };

  const closeElevation = () => {
    if (highlightLayerRef.current) {
      highlightLayerRef.current.clearLayers();
    }
    if (drawnedLine) {
      mapRef.current.removeLayer(drawnedLine.layer);
    }
    setCellElevationChart(false);
  };

  const handleSuggestionSelect = (clickedSuggestion /*type: Suggestion*/) => {
    switch(clickedSuggestion.type) {
      case 'site':
        handleSiteClicked(
          null,
          clickedSuggestion.suggestionItem,
          clickedSuggestion.siteIndex,
          true /*flyToIt*/
        );
        break;
      case 'cell':
        handleCellClicked(
          null /*mouseEvent*/,
          clickedSuggestion.suggestionItem,
          'cell' /*type*/,
          true /*flyToIt*/
        );
        break;
      case 'geoJsonFeature': {
        const { layerId, feature } = clickedSuggestion.suggestionItem;
        try {
          // Find the Leaflet feature layer that corresponds to this suggestion.
          // Leaflet stores a reference to the original feature object on each
          // child layer (fl.feature), so we can match by reference.
          const parentLayer = customGeoJsonLayersRef.current[layerId];
          let matchedLayer = null;
          if (parentLayer) {
            parentLayer.eachLayer((fl) => {
              if (fl.feature === feature) matchedLayer = fl;
            });
          }

          if (matchedLayer) {
            handleGeoJsonFeatureClick(feature, matchedLayer);
          }

          // Fly the map to the feature regardless of whether a layer was found
          const bounds = L.geoJSON(feature).getBounds();
          if (bounds.isValid()) {
            mapRef.current.fitBounds(bounds, { maxZoom: 16, padding: [40, 40] });
          }
        } catch (e) {
          console.error('Could not select GeoJSON feature from search', e);
        }
        break;
      }
      case 'mapPlace':
        geoJsonLayerRef.current?.clearLayers();
        if (clickedSuggestion.suggestionItem.geojson.type === 'Point') {
          const circle = L.circle(
            [
              clickedSuggestion.suggestionItem.geojson.coordinates[1],
              clickedSuggestion.suggestionItem.geojson.coordinates[0]
            ],
            {
              radius: 10,
              color: 'blue',
              weight: 2,
              fillOpacity: 0.1,
            }
          )
            .addTo(mapRef.current)
            .on('pm:enable', (e) => e.target.pm.disable());
          mapRef.current.fitBounds(circle.getBounds());
        } else {
          const polygonLayer = L.geoJSON(clickedSuggestion.suggestionItem.geojson, {
            style: {
              color: 'blue',
              weight: 2,
              fillOpacity: 0.1,
            },
          })
            .addTo(mapRef.current)
            .on('pm:enable', (e) => e.target.pm.disable());

          const markersInside = checkMarkersInsidePolygon(
            polygonLayer,
            markersListAfterCellFilters
          );

          setSelectedMarkers(extractCells(markersInside));
          mapRef.current.fitBounds(polygonLayer.getBounds());
          showModal();
          geoJsonLayerRef.current = polygonLayer;
        }
        break;
      default:
        console.error(`Not recognized clicked searched suggestion type [{clickedSuggestion.type}]`);
    };
    /*
    
      
    }
      */
  };

  const showBands = (bandNames) => {
    if (!mapRef.current) return;
    bandsList.forEach((bandFilterItem) => {
      if (bandNames.includes(bandFilterItem.id)) {
        if (
          !mapRef.current.hasLayer(typeLayersRef.current[bandFilterItem.id])
        ) {
          mapRef.current.addLayer(typeLayersRef.current[bandFilterItem.id]);
        }
      } else {
        if (mapRef.current.hasLayer(typeLayersRef.current[bandFilterItem.id])) {
          mapRef.current.removeLayer(typeLayersRef.current[bandFilterItem.id]);
        }
      }
    });
  };

  const createTileLayer = (url, options = {}) => {
    return L.tileLayer(url, { ...options, ...{ opacity: 0.5 } });
  };

  const getTileLayer = (map, selectedTileLayer) => {
    switch (selectedTileLayer.toLowerCase()) {
      case 'standard':
        tileLayerRef.current = createTileLayer(TILE_LAYER_URLS.standard);
        break;
      case 'humanitarian':
        tileLayerRef.current = createTileLayer(TILE_LAYER_URLS.humanitarian);
        break;
      case 'bing grey':
        tileLayerRef.current = L.tileLayer.bing({
          ...bingLayerOptions,
          bingMapsKey: TILE_LAYER_URLS.bingGreyApiKey,
        });
        break;
      default:
        console.warn('Invalid layer selected.');
        return;
    }
    if (map) {
      tileLayerRef.current?.addTo(map);
    }
  };
  // eslint-disable-next-line react/prop-types
  const MapEvents = ({ geoJsonLayersLoaded }) => {
    const map = useMap();
    useEffect(() => {
      if (map && filtersLoaded) {
        if (!mainLayerRef.current) {
          mainLayerRef.current = L.layerGroup([], {
            renderer: L.canvas(),
          }).addTo(map);

          circleLayerRef.current = L.layerGroup([], {
            renderer: L.canvas(),
          }).addTo(map);

          highlightLayerRef.current = L.layerGroup([], {
            renderer: L.canvas(),
          }).addTo(map);

          highlightCellLayerRef.current = L.layerGroup([], {
            renderer: L.canvas(),
          }).addTo(map);

          elementDetailsLayerRef.current = L.layerGroup([], {
            renderer: L.canvas(),
          }).addTo(map);

          typeLayersRef.current = bandsList.reduce((acc, bandFilterItem) => {
            acc[bandFilterItem.id] = L.layerGroup([], {
              renderer: L.canvas(),
            }).addTo(map);
            return acc;
          }, {});

          globalLayersLeafletLayerGroupRef.current = L.layerGroup([], {
            renderer: L.canvas(),
            zIndex: 100,
          }).addTo(map);

          const globalSelectedLayers = reduxGlobalLayers?.filter(
            (el) => el.selected == true
          );
          if (
            globalSelectedLayers &&
            !geoJsonLayersLoaded.current &&
            globalSelectedLayers.length > 0
          ) {
            globalSelectedLayers.map((layer, index) => {
              let cachedData = null;
              checkIfCacheExists(layer.LayerName).then((data) => {
                if (data) {
                  // Add cached data to your layer here
                  cachedData = data;
                  layersLoadedFromCacheRef.current.push(layer.LayerName);
                }
                handleLayersDataEvents(true, layer);
              });
            });
            geoJsonLayersLoaded.current = true;
          }

          // Initialize marker clustering
          updateLayers = () => {
            let filteredKpi =
              kpiFiltered.selectedValues.length > 0 &&
              kpiFiltered.markers.length > 0;
            if (!map._mapPane) return;
            const bounds = map.getBounds();
            mapBoundsRef.current = bounds;
            const zoomLevel = map.getZoom();

            if (markersListAfterCellFilters.length > 0) {
              visibleMarkers = [];

              markersListAfterCellFilters.forEach((siteMarkerInfo) => {
                // first check if this SITE is OUTSIDE BOUNDS
                if (!bounds.contains(siteMarkerInfo.location)) {
                  // this SITE is outside boindaries, so we perform just the
                  // cleanup to remove site and all the thigns from the map
                  siteMarkerInfo['sitePointMarker']?.remove();
                  siteMarkerInfo.orientations.forEach(
                    (orientationMarkerInfo) => {
                      orientationMarkerInfo['azimuthLineMarker']?.remove();
                      orientationMarkerInfo.markers.forEach(
                        (cellMarkerInfo) => {
                          cellMarkerInfo['cellTriangleMarker']?.remove();
                        }
                      );
                    }
                  );
                } else {
                  // SITE WITHIN Boundaries
                  visibleMarkers.push(siteMarkerInfo);

                  // chck if Leaflet marker was already created
                  if (!siteMarkerInfo['sitePointMarker']) {
                    const circle = new LeafletSiteMarkerCircle(
                      siteMarkerInfo,
                      highlightedCriteriasRef,
                      handleSiteClicked
                    );
                    siteMarkerInfo['sitePointMarker'] = circle;
                  } // end siteMarker created
                  circleLayerRef.current.addLayer(
                    siteMarkerInfo['sitePointMarker']
                  );

                  // we will use this variables to store all the disting site names that exists in this location
                  let locationLabels = [];
                  // Draw the edges and triangles
                  let zommValidForTriangleRender =
                    zoomLevel > triangleZoomThreshold;
                  siteMarkerInfo.orientations.forEach(
                    (orientationMarkerInfo) => {
                      if (zommValidForTriangleRender) {
                        // check if orientation marker icon is already created
                        if (!orientationMarkerInfo['azimuthLineMarker']) {
                          const azimuthLineMarker =
                            new LeafletAzimuthMarkerLine(
                              orientationMarkerInfo,
                              siteMarkerInfo,
                              highlightedCriteriasRef
                            );
                          orientationMarkerInfo['azimuthLineMarker'] =
                            azimuthLineMarker;
                        }
                        mainLayerRef.current.addLayer(
                          orientationMarkerInfo['azimuthLineMarker']
                        );
                      } else {
                        // zoom invalid, so remove layer
                        orientationMarkerInfo['azimuthLineMarker']?.remove();
                      }

                      // itarate over each Band/Cell
                      orientationMarkerInfo.markers.forEach(
                        (cellMarkerInfo) => {
                          // check if this cell is associated to a site for which we havent collected it's name
                          // for the "site label" tooltip
                          if (
                            siteIdentifierKey != 'None' &&
                            !locationLabels.contains(
                              cellMarkerInfo[[siteIdentifierKey]]
                            )
                          ) {
                            locationLabels.push(
                              cellMarkerInfo[[siteIdentifierKey]]
                            );
                          }

                          // check if the the CELL marker is aleardy created
                          if (zommValidForTriangleRender) {
                            if (!cellMarkerInfo['cellTriangleMarker']) {
                              const cellTriangleMarker =
                                new LeafletCellMarkerReprPoly(
                                  cellMarkerInfo,
                                  highlightedCriteriasRef,
                                  clickedCellRef
                                ).on('click', (e) => {
                                  if (mapPopupState.isVisible) return;
                                  handleCellClicked(e, cellMarkerInfo);
                                });
                              cellMarkerInfo['cellTriangleMarker'] =
                                cellTriangleMarker;
                            } // en triangle marker creation
                            typeLayersRef.current[
                              cellMarkerInfo.bandFilterItemId
                            ].addLayer(cellMarkerInfo['cellTriangleMarker']);
                          } else {
                            // zoom invalid, so remove layer
                            cellMarkerInfo['cellTriangleMarker']?.remove();
                          }

                          // CELL LABEL
                          if (
                            zoomLevel > labelsZoomThreshold &&
                            cellIdentifierKey !== 'None'
                          ) {
                            cellMarkerInfo['cellTriangleMarker'].bindTooltip(
                              cellMarkerInfo[cellIdentifierKey],
                              {
                                permanent: true,
                                direction: 'bottom',
                                className: 'cell-indicator-marker',
                                opacity: 1,
                              }
                            );
                          } else {
                            // out of LABEL ZOOM, or no Label attribute set, then remove tooltip
                            cellMarkerInfo[
                              'cellTriangleMarker'
                            ]?.unbindTooltip();
                          }

                          // KPI halo
                          // remove the KPI halo if exists
                          // TODO more enhancement to not-re-create
                          // dont draw halos if we are in Data profiles popup view
                          cellMarkerInfo['cellKpiHaloMarker']?.remove();
                          if (
                            !mapPopupState.isVisible &&
                            filteredKpi &&
                            zommValidForTriangleRender
                          ) {
                            let filteredMarkers = kpiFiltered.markers.find(
                              (m) => m.id === cellMarkerInfo.id
                            );
                            if (filteredMarkers) {
                              const cellShiftedLatLng =
                                calculateOrientationShiftedPosition(
                                  map,
                                  cellMarkersReprPolySizesPerZoom[
                                    zoomLevel
                                  ],
                                  siteMarkerInfo.location,
                                  orientationMarkerInfo.orientation,
                                  cellMarkerInfo.cellIndexWithinOrientation
                                );
                              const bandRepresentation = L.sector({
                                center: cellShiftedLatLng,
                                startBearing:
                                  orientationMarkerInfo.orientation - 70,
                                endBearing:
                                  orientationMarkerInfo.orientation + 70,
                                innerRadius:
                                  cellMarkersReprPolySizesPerZoom[
                                    zoomLevel
                                  ] /
                                    2 -
                                  2,
                                outerRadius:
                                  cellMarkersReprPolySizesPerZoom[
                                    zoomLevel
                                  ] /
                                    2 +
                                  2,
                                fillColor: filteredMarkers.THRESHOLD_COLOR,
                                fillOpacity: 0.4,
                                color: filteredMarkers.THRESHOLD_COLOR,
                                rhumb: true,
                                weight: 1,
                              }).on('click', (e) =>
                                handleCellClicked(
                                  e,
                                  {
                                    ...cellMarkerInfo,
                                    contextInfo: {
                                      ...cellMarkerInfo.contextInfo,
                                      ...filteredMarkers.contextInfo,
                                    },
                                  },
                                  'band'
                                )
                              );
                              typeLayersRef.current[
                                cellMarkerInfo.bandFilterItemId
                              ].addLayer(bandRepresentation);
                              cellMarkerInfo['cellKpiHaloMarker'] =
                                bandRepresentation;
                            }
                          }
                        }
                      );
                    }
                  );

                  // SITE LABEL
                  if (
                    zoomLevel > labelsZoomThreshold &&
                    locationLabels.length > 0
                  ) {
                    siteMarkerInfo['sitePointMarker'].bindTooltip(
                      locationLabels.join('<br>'),
                      {
                        permanent: true,
                        direction: 'bottom',
                        className: 'site-indicator-marker',
                        opacity: 1,
                      }
                    );
                  } else {
                    // out of LABEL ZOOM, or no Label attribute set, then remove tooltip
                    siteMarkerInfo['sitePointMarker'].unbindTooltip();
                  }
                } // end if SITE marker is in map bounds
              }); // end of full markers iterations
              if (mapPopupState.geoJson && !geoJsonLayerRef.current) {
                const polygonLayer = L.geoJSON(mapPopupState.geoJson, {
                  style: {
                    color: '#06345A', // The stroke color of the shape
                    fillColor: '#ffe1c6', // The fill color of the shape (for polygons)
                    fillOpacity: 0.41, // The opacity of the fill color
                    weight: 2, // The stroke width of the shape
                  },
                }).addTo(map);

                polygonLayer.on('pm:update', (event) => {
                  const markersInside = checkMarkersInsidePolygon(
                    event.layer,
                    visibleMarkers
                  );
                  setSelectedMarkers(extractCells(markersInside));
                  showModal();
                  dispatch(
                    setMapPopupState({
                      isVisible: mapPopupState.isVisible,
                      getGeoJsonCallback: mapPopupState.getGeoJsonCallback,
                      geoJson: event.layer.toGeoJSON().geometry,
                      region: selectedRegionId,
                    })
                  );
                });

                polygonLayer.on('pm:cut', (event) => {
                  const markersInside = checkMarkersInsidePolygon(
                    event.layer,
                    visibleMarkers
                  );
                  setSelectedMarkers(extractCells(markersInside));
                  showModal();
                  dispatch(
                    setMapPopupState({
                      isVisible: mapPopupState.isVisible,
                      getGeoJsonCallback: mapPopupState.getGeoJsonCallback,
                      geoJson: event.layer.toGeoJSON().geometry,
                      region: selectedRegionId,
                    })
                  );
                });

                polygonLayer.on('pm:remove', (event) => {
                  dispatch(
                    setMapPopupState({
                      isVisible: mapPopupState.isVisible,
                      getGeoJsonCallback: mapPopupState.getGeoJsonCallback,
                      geoJson: null,
                      region: null,
                    })
                  );
                });
                geoJsonLayerRef.current = polygonLayer;

                if (mapRef.current) {
                  let coordinates = mapPopupState.geoJson.geometry
                    ? mapPopupState.geoJson.geometry.coordinates
                    : mapPopupState.geoJson.coordinates;

                  // Original polygon coordinates in [longitude, latitude] format
                  // Swap each coordinate to [latitude, longitude] format
                  const correctedCoordinates = coordinates.flatMap((coords) =>
                    coords.map((coord) => [coord[1], coord[0]])
                  );
                  mapRef.current.flyToBounds(
                    correctedCoordinates,
                    mapRef.current.getZoom()
                  );
                }
              }
            }
            dispatch(
              setMapPosition({
                zoom: zoomLevel,
                center: [map.getCenter().lat, map.getCenter().lng],
              })
            );
          };

          updateLayers();

          const debouncedUpdateMarkersInView = debounceFn(
            (triggerType) => updateLayers(triggerType),
            100
          );

          map.on('moveend', () => debouncedUpdateMarkersInView(MOVE));
          map.on('zoomend', () => debouncedUpdateMarkersInView(ZOOM));
        }
      }
      map.pm.addControls({
        position: 'topleft',
        drawCircleMarker: false,
        drawCircle: !mapPopupState.isVisible,
        drawPolyline: !mapPopupState.isVisible,
        drawMarker: false,
        rotateMode: false,
        oneBlock: true,
        drawText: false,
      });
      map.pm.setPathOptions({
        color: '#06345A', // The stroke color of the shape
        fillColor: '#ffe1c6', // The fill color of the shape (for polygons)
        fillOpacity: 0.41, // The opacity of the fill color
        weight: 2, // The stroke width of the shape
      });

      const handleCreate = (e) => {
        let layer = e.layer;
        layer.on('pm:remove', () => {
          markerTableRef.current.mapPopupRef.current.handleOpenModal(false);
        });
        if (e.shape === 'Circle') {
          const radius = layer.getRadius(); // Radius in meters
          const diameter = (radius * 2) / 1000; // Convert diameter to kilometers

          // Set up a popup to display the diameter
          const popupContent = `Diameter: ${diameter.toFixed(2)} km`;
          layer.bindPopup(popupContent).openPopup();
          layer.on('popupclose', () => {
            map.removeLayer(layer); // Remove the layer from the map when the popup is closed
          });
        } else if (e.shape === 'Line') {
          // Calculate the length of the polyline
          const latlngs = layer.getLatLngs();
          let totalDistance = 0;

          const linePoints = splitLine(
            latlngs[0].lat,
            latlngs[0].lng,
            latlngs[1].lat,
            latlngs[1].lng,
            10
          );
          const midpoint = calculateMidpoint(latlngs);
          const pixelPos = map.latLngToContainerPoint(midpoint);
          for (let i = 0; i < latlngs.length - 1; i++) {
            const segmentDistance = map.distance(latlngs[i], latlngs[i + 1]);
            totalDistance += segmentDistance;
          }

          totalDistance = totalDistance / 1000; // Convert to kilometers

          setDrawnedLine({ layer, totalDistance, linePoints });
          openContextMenuAt(pixelPos);

          layer.on('popupclose', () => {
            map.removeLayer(layer);
          });
        } else {
          dispatch(
            setMapPopupState({
              isVisible: mapPopupState.isVisible,
              getGeoJsonCallback: mapPopupState.getGeoJsonCallback,
              geoJson: layer.toGeoJSON().geometry,
              region: selectedRegionId,
            })
          );

          const markersInside = checkMarkersInsidePolygon(
            layer,
            visibleMarkers
          );
          setSelectedMarkers(extractCells(markersInside));
          showModal();

          layer.on('pm:update', (event) => {
            const markersInside = checkMarkersInsidePolygon(
              layer,
              visibleMarkers
            );
            setSelectedMarkers(extractCells(markersInside));
            showModal();
          });
          layer.on('pm:cut', (event) => {
            const markersInside = checkMarkersInsidePolygon(
              event.layer,
              visibleMarkers
            );
            setSelectedMarkers(extractCells(markersInside));
            showModal();
          });
          layer.on('click', () => {
            setSelectedMarkers(extractCells(markersInside));
            showModal();
          });
        }
      };
      map.on('pm:create', handleCreate);
      return () => {
        map.off('pm:create', handleCreate);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    mapRef.current = map;
    return null;
  };

  const applyIdentifierChange = () => {
    if (filtersState.length > 0) {
      cellIdentifierKey = filtersState?.find(
        (filter) => filter.filterId === 'CELL_IDENTIFIER'
      ).selectedValues[0].name;
      siteIdentifierKey = filtersState?.find(
        (filter) => filter.filterId === 'SITE_IDENTIFIER'
      ).selectedValues[0].name;
    }
    if (map && updateLayers) {
      updateLayers();
    }
  };
  const fetchFilterData = async (
    filter,
    filtersData,
    locationFilter,
    kqiValues
  ) => {
    filtersData.datafilters = [
      granularityDatesRef.current,
      {
        filterId: 'global_technology_filter',
        values: [
          {
            id: filter.filterId === 'KPI_LTE' ? 'LTE' : 'NR',
            name: filter.filterId === 'KPI_LTE' ? 'LTE' : 'NR',
          },
        ],
      },
      {
        filterId: 'global_kpi_filter',
        values: [
          {
            id: filter.selectedValues[0].id,
            name: filter.selectedValues[0].name,
          },
        ],
      },
      {
        filterId: 'global_region_filter',
        values: [
          {
            id: locationFilter.selectedValues[0]?.id,
            name: locationFilter.selectedValues[0]?.name,
          },
        ],
      },
      {
        filterId: 'global_kqi_filter',
        values: kqiValues,
      },
    ];

    if (filter.filterId === 'KPI_LTE') {
      kpiLTEPropertyRef.current = {
        id: filter.selectedValues[0]?.id,
        name: filter.selectedValues[0]?.name,
      };
    } else {
      kpiNRPropertyRef.current = {
        id: filter.selectedValues[0]?.id,
        name: filter.selectedValues[0]?.name,
      };
    }

    const filterResponse = await loadMarkers(filtersData, false);
    if (!kpiFiltered.selectedValues.includes(filter.selectedValues[0].id)) {
      kpiFiltered.selectedValues.push(filter.selectedValues[0].id);
    }

    const mergedMarkersMap = new Map();

    kpiFiltered.markers.forEach((marker) => {
      mergedMarkersMap.set(marker.id, marker);
    });

    filterResponse.arrayMarkers.forEach((marker) => {
      if (mergedMarkersMap.has(marker.id)) {
        // Merge existing marker with the new one
        const existingMarker = mergedMarkersMap.get(marker.id);
        mergedMarkersMap.set(marker.id, { ...existingMarker, ...marker });
      } else {
        // Add new marker if it doesn't exist
        mergedMarkersMap.set(marker.id, marker);
      }
    });

    kpiFiltered.markers = Array.from(mergedMarkersMap.values());

    if (map && updateLayers) {
      updateLayers();
    }
    return { filterId: filter.filterId, filterResponse, filtersData };
  };

  const fetchTechnologyKpiData = async (filter) => {
    let filtersData = {
      podId: 'ni-ran-advanced-map-dashboard-cell-kpi',
    };
    let locationFilter = filtersState.find(
      (filter) => filter.category === 'regionName'
    );
    const kqiFilter = filtersState.find(
      (filter) => filter.category === 'mapKqiFilter'
    );

    const kqiValues =
      kqiFilter && kqiFilter.selectedValues.length > 0
        ? kqiFilter.selectedValues.map((el) => ({
            id: el.id ?? '',
            name: el.name,
          }))
        : [];
    kqiFiltersSelected.current = kqiValues;

    try {
      // Fetch filter data sequentially
      await fetchFilterData(filter, filtersData, locationFilter, kqiValues);
    } catch (error) {
      console.error('Error fetching server filters:', error);
    }
  };
  const applySettingsClicked = async () => {
    // after apliny the settings popup check of we need to reload:
    // -  KPIs
    // Neighbour
    const isKPILayerSelected = reduxGlobalLayers.find(
      (el) => el.LayerName == 'Technology KPI' && el.selected == true
    );
    if (isKPILayerSelected) {
      fetchTechnologiesKPIsData();
    }
    // reload element layers if any
    for (const [_, activeElementLayerDefinition] of Object.entries(
      activeElementDetailsLayers
    )) {
      handleElementDetailsLayerClicked(
        activeElementLayerDefinition.networkElementMarkerInfo,
        activeElementLayerDefinition
      );
    }

    applyIdentifierChange();
    applyCellFilters();
  };

  const fetchTechnologiesKPIsData = async () => {
    setLoading(true);
    handleCloseCellDetails();
    await Promise.all(
      filtersState
        .filter((filter) => filter.category === 'mapKpiFilter')
        .map((filter) => fetchTechnologyKpiData(filter))
    );

    setLoading(false);
  };

  const removeTechnologiesKPIsData = async () => {
    // clean markers
    // and clean leayers
    kpiFiltered.markers = [];
    kpiFiltered.selectedValues = [];

    visibleMarkers.forEach((siteMarkerInfo) =>
      siteMarkerInfo.orientations.forEach((orientationMarkerInfo) =>
        orientationMarkerInfo.markers.forEach((cellMarkerInfo) =>
          cellMarkerInfo['cellKpiHaloMarker']?.remove()
        )
      )
    );
  };

  const drawElementDetailsLayerData = (geoJson, layerDefinition) => {
    // check if the layer already exists
    let polygonLayer = null;
    elementDetailsLayerRef.current.eachLayer((layer) => {
      if (layer.options.elementDetailLayerId === layerDefinition.LayerId) {
        polygonLayer = layer;
        layer.clearLayers();
        layer.addData(geoJson);
      }
    });
    if (!polygonLayer) {
      // extract the layer definition default color in case need to use it
      let layerDefinitionFirstColor;
      if (layerDefinition.LayerProperties.LegendValueToColorMapping) {
        layerDefinitionFirstColor = Object.values(
          layerDefinition.LayerProperties.LegendValueToColorMapping
        )[0];
      }
      // standard geojson styled rendering
      polygonLayer = L.geoJSON(geoJson, {
        pointToLayer: (feature, latlng) => {
          // Handle point geometries with circle markers
          if (feature.geometry.type === 'Point') {
            const style = feature.style || {}; // Default to empty if no style in feature

            // TODO: TO BE MOVED TO A SEPARATE CLASS
            if (layerDefinition.LayerId === 'ELEMENT_DETAILS_PROXIMITY_ANALYSIS') {
              // Orientation angle in degrees (0 = East, 90 = North, etc.)
              const orientationAngle = parseInt(feature.properties['hidden_azimuth']); // Change this to set the direction of the fan
              return L.sector({
                center: [
                  parseFloat(feature.properties['hidden_latitude']),
                  parseFloat(feature.properties['hidden_longitude'])
                ],
                innerRadius: parseFloat(feature.properties['hidden_min_ue_range_per_meter']),
                outerRadius: parseFloat(feature.properties['hidden_max_ue_range_per_meter']),
                startBearing: orientationAngle - 40 / 2,
                endBearing: orientationAngle + 40 / 2,
                fillColor: style.fill || style.fillColor || 'red',
                fillOpacity: style.fillOpacity || 0.8,
                color:
                  style.stroke ||
                  style.color ||
                  style.strokeColor ||
                  style.fillColor ||
                  '#000000', // Default stroke color
                weight: style.weight || 1, // Default stroke weight
                });
            } else {
              // Standard GeoJson Point layer using Circle
              return L.circleMarker(latlng, {
                radius: style.radius || 8, // Default radius
                fillColor: style.fill || layerDefinitionFirstColor || '#398e3d', // Default fill color
                fillOpacity: style.fillOpacity || 0.8, // Default fill opacity
                color:
                  style.stroke ||
                  style.color ||
                  style.strokeColor ||
                  style.fillColor ||
                  '#000000', // Default stroke color
                weight: style.weight || 1, // Default stroke weight
                });
            }
          }
          // For non-point geometries, return null so `pointToLayer` does not interfere
          return null;
        },
        onEachFeature: (feature, layer) => {
          if (
            feature.geometry.type === 'Polygon' ||
            feature.geometry.type === 'MultiPolygon' ||
            feature.geometry.type === 'LineString'
          ) {
            // Set the style for polygons
            const style = feature.style || {};

            // Apply the style if setStyle is available for the layer
            if (layer.setStyle) {
              layer.setStyle({
                fillColor:
                  style.fillColor || layerDefinitionFirstColor || '#398e3d',
                fillOpacity: style.fillOpacity || 0.8,
                color:
                  style.color ||
                  style.strokeColor ||
                  style.fillColor ||
                  '#000000',
                weight: style.weight || 2,
              });
            }
          }
          if (feature.properties) {
            const popupContent = Object.entries(feature.properties)
              .filter(([key, value]) => key.indexOf('hidden_') === -1)
              .map(([key, value]) => {
                let propertyHtmlValue = '';
                if (key === 'KPI_THRESHOLD_VALUES') {
                  // special handling for threshold values legend
                  value.map((item) => {
                    propertyHtmlValue += `
                  <div key=${item.name} class="legend-entry">
                    <div
                      class="square"
                      style="background-color: ${
                        item.color ? item.color : '#fff'
                      }"
                    ></div>
                    <div><strong>${item.name}:</strong> ${item.value}</div>
                  </div>
                  `;
                  });
                } else {
                  // any other property
                  propertyHtmlValue = `<strong>${key}:</strong> ${
                    value ?? 'N/A'
                  }`;
                }
                return propertyHtmlValue;
              })
              .join('<br>');
            const popupOptions = {
              maxWidth: 300,
              className: 'custom-popup',
            };
            layer.bindPopup(popupContent, popupOptions);
          }
        },
        elementDetailLayerId: layerDefinition.LayerId,
      });
      
      elementDetailsLayerRef.current.addLayer(polygonLayer);
    }
  };

  const handleElementDetailsLayerClicked = async (
    networkElementMarkerInfo,
    elementDetailLayerDefinition
  ) => {
    setLoading(true);

    // Fetch new data from the server based on the updated granularityDates
    try {
      const elementDetailsLayerData = await fetchElementDetailsLayerData(
        networkElementMarkerInfo,
        granularityDatesRef.current,
        elementDetailLayerDefinition,
        filtersState
      );
      // If we received valid data, draw it and update the cmLayers state
      if (elementDetailsLayerData.features.length > 0) {
        drawElementDetailsLayerData(
          elementDetailsLayerData,
          elementDetailLayerDefinition
        );
        activeElementDetailsLayers[elementDetailLayerDefinition.LayerId] = {
          ...elementDetailLayerDefinition,
          networkElementMarkerInfo: { ...networkElementMarkerInfo },
        };
        setActiveElementDetailsLayers((prevState) => {
          prevState[elementDetailLayerDefinition.LayerId] = {
            ...elementDetailLayerDefinition,
            networkElementMarkerInfo: { ...networkElementMarkerInfo },
          }
          return { ...prevState };
        });
      } else {
        // if no data, then clear the layer
        hideElementDetailLayer(
          networkElementMarkerInfo,
          elementDetailLayerDefinition
        );
        const alertData = {
          severity: 'warning',
          title: 'No Data',
          message:
            'No data found for "' +
            elementDetailLayerDefinition.LayerName +
            '" layer the selected element and date range.',
          open: true,
        };
        dispatch(showAlert(alertData));
      }
    } catch (e) {
      // if error then clear the layer information
      hideElementDetailLayer(
        networkElementMarkerInfo,
        elementDetailLayerDefinition
      );
      let errorMsg =
        'Error while retrieving "' +
        elementDetailLayerDefinition.LayerName +
        '" layer data.';
      console.error(errorMsg, e);
      const alertData = {
        severity: 'error',
        title: 'Data error',
        message: errorMsg,
        open: true,
      };
      dispatch(showAlert(alertData));
    }

    setLoading(false);
  };

  const hideElementDetailLayer = (
    networkElementMarkerInfo,
    elementDetailLayerDefinition
  ) => {
    setActiveElementDetailsLayers((prevState) => {
      delete prevState[elementDetailLayerDefinition.LayerId];
      return { ...prevState };
    });
    elementDetailsLayerRef.current.eachLayer((layer) => {
      if (
        layer.options.elementDetailLayerId ===
        elementDetailLayerDefinition.LayerId
      ) {
        layer.removeFrom(elementDetailsLayerRef.current);
      }
    });
  };

  const flyToCoordinates = (coordinates, zoomLevel = 10) => {
    const latLngs = [coordinates];
    const latLngObjects = latLngs.map((latLng) => L.latLng(latLng));
    const bounds = L.latLngBounds(latLngObjects);
    const center = bounds.getCenter();
    if (mapRef.current) {
      mapRef.current.flyTo(center, zoomLevel); // Create the bounds
    }
  };

  const applyRegionSelection = (flyto = false) => {
    markerTableRef.current.mapPopupRef.current.handleOpenModal(false);
    let locationFilter = filtersState.find(
      (filter) => filter.category === 'regionName'
    );
    selectedRegionId = locationFilter.selectedValues[0]?.id;
    const filtersData = {
      datafilters: [
        {
          filterId: 'global_region_filter',
          values: [
            {
              id: locationFilter.selectedValues[0]?.id,
              name: locationFilter.selectedValues[0]?.name,
            },
          ],
        },
      ],
    };
    // iterate over global layers and update filters
    globalLayersLeafletLayerGroupRef.current?.eachLayer(function (layer) {
      if (layer['filtersChanged']) {
        layer.filtersChanged(filtersState, granularityDatesRef.current);
      }
    });

    loadMarkers(filtersData).then(function () {
      if (flyto) {
        const coordinates = [
          markersListFromServer[0].location.lat,
          markersListFromServer[0].location.lng,
        ];
        flyToCoordinates(coordinates);
      }
    });
  };

  const applyCellFilters = () => {
    // flatenned the filters and transform them to same property names of the cell
    // example filterId is CELL_CATEGORY but attribute in cell is "CELL CATEGORY"
    let flattenedCellFilters = {};
    filtersState.forEach((filter) => {
      if (filter.category === 'localFilter') {
        flattenedCellFilters[filter.filterId.replaceAll('_', ' ')] =
          filter.selectedValues.map((value) => value.name);
      }
    });

    // take the original marker list and remove the cells that are
    // no longer valid
    markersListAfterCellFilters = [];
    // check if the clickedCell is still valid
    let elementClickedValid = false;
    let elementClickedIsSite = clickedCellRef.current?.type === 'site';

    markersListFromServer.forEach((siteMarkerInfo) => {
      let clonnedSiteToOutput = null;
      siteMarkerInfo.orientations.forEach((orientationMarkerInfo) => {
        let clonnedOrientation = null;
        orientationMarkerInfo.markers.forEach((cellMarkerInfo) => {
          let isCellValid = Object.entries(flattenedCellFilters).every(
            ([filterId, filterValues]) =>
              filterValues.includes(cellMarkerInfo[filterId])
          );
          // if at least 1 cell of orientatino is valid, then the orientation and site are valid
          // we create the site and orientation of not already and add the cell to the orientation
          if (isCellValid) {
            if (!clonnedSiteToOutput) {
              clonnedSiteToOutput = {
                ...siteMarkerInfo,
                orientations: new Map(),
              };
              markersListAfterCellFilters.push(clonnedSiteToOutput);
            }
            if (!clonnedOrientation) {
              clonnedOrientation = { ...orientationMarkerInfo, markers: [] };
              clonnedSiteToOutput.orientations.set(
                clonnedOrientation.orientation,
                clonnedOrientation
              );
            }
            clonnedOrientation.markers.push(cellMarkerInfo);
            // verify if either this site or cell is the clicked element
            elementClickedValid =
              elementClickedValid ||
              // site check based on lat/long
              (elementClickedIsSite &&
                clickedCellRef.current?.location.lat ==
                  clonnedSiteToOutput.location.lat &&
                clickedCellRef.current?.location.lng ==
                  clonnedSiteToOutput.location.lng) ||
              // cell check based on cell_natural_key
              (!elementClickedIsSite &&
                clickedCellRef.current?.id == cellMarkerInfo.id);
          }
        });
      });
    });

    if (updateLayers) {
      // check if we have the cell pane and if so, check if the eleemnt
      // site/cell is still visible after filteing, if so, leave it opened
      if (!elementClickedValid) handleCloseCellDetails();
      // clear layers to be redrawn
      mainLayerRef.current.clearLayers();
      circleLayerRef.current.clearLayers();
      Object.values(typeLayersRef.current).forEach((layer) => {
        layer.clearLayers();
      });
      updateLayers();
    }
  };

  const detailsPerformanceDetails = (cell) => {
    if (clickedCell.type === 'site') {
      performanceDetailsSite(cell);
    } else {
      performanceDetailsCell(cell);
    }
  };

  const applyFiltersFromUrl = () => {
    // Get the current URL
    if (hashIndex !== -1 && queryString) {
      // Parse the query parameters using URLSearchParams
      const searchParams = new URLSearchParams(queryString);

      if (searchParams.get('region')) {
        dispatch(
          changeFilterValues({
            filterId: 'REGION_NAME',
            name: searchParams.get('region'),
          })
        );
      }
      if (
        searchParams.get('dateValue') &&
        searchParams.get('dateAggregation')
      ) {
        let dateValue = searchParams.get('dateValue');
        let range = dateValue.includes('|') ? dateValue : `${dateValue}|${dateValue}`;
        let dateAggregation = searchParams.get('dateAggregation');

        let fromHour,toHour;
        let fromMinute,toMinute;
        if(dateValue.includes('|')){
          let dates = dateValue.split("|");
          let fromDate = getBrowserDateShiftedToUserTZ(new Date(dates[0]));
          let toDate = getBrowserDateShiftedToUserTZ(new Date(dates[1]));
          fromHour = fromDate.getHours();
          fromMinute = fromDate.getMinutes();

          toHour = toDate.getHours();
          toMinute = toDate.getMinutes();
        }
        else{
          let date = getBrowserDateShiftedToUserTZ(new Date(dateValue));
          fromHour = toHour = date.getHours();
          fromMinute = toMinute = date.getMinutes();
        }
       
        dispatch(
          setGranularityDates({
            filterId: 'global_date_filter',
            values: [
              {
                id: range,
              },
              {
                id: dateAggregation,
              },
              {
                id: `${fromHour}:${fromMinute}|${toHour}:${toMinute}`,
              },
            ],
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    loadDashboardDescriptor(dashboardId);

    return () => {
      cleanUpMap(mapRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // the band list was loaded, which means all filter sinitialized
    // then do INITIAL MARKERS LOAD
    if (bandsList.length === 0 || !filtersLoaded) return;

    applyRegionSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandsList, filtersLoaded]);

  useEffect(() => {
    showBands(selectedBands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBands]);

  useEffect(() => {
    granularityDatesRef.current = granularityDates;

    const isKPILayerSelected = reduxGlobalLayers.find(
      (el) => el.LayerName == 'Technology KPI' && el.selected == true
    );

    if (isKPILayerSelected) {
      fetchTechnologiesKPIsData().then(() => {
        setLoading(false);
      });
    }

    // reload element layers if any
    for (const [_, activeElementLayerDefinition] of Object.entries(
      activeElementDetailsLayers
    )) {
      handleElementDetailsLayerClicked(
        activeElementLayerDefinition.networkElementMarkerInfo,
        activeElementLayerDefinition
      );
    }
    // iterate over global layers and update filters
    globalLayersLeafletLayerGroupRef.current?.eachLayer((layer) => {
      if (layer['filtersChanged']) {
        layer.filtersChanged(filtersState, granularityDates);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularityDates]);

  useEffect(() => {
    // iterate over all sitelocation markers, all orientations and all cell
    // markers to check if needs highlight or disable highlight
    highlightedCriteriasRef.current = highlightedCriterias;
    clickedCellRef.current = clickedCell;
    visibleMarkers.forEach((siteMarkerInfo) => {
      siteMarkerInfo['sitePointMarker']?.verifyHighlightCriteria();
      siteMarkerInfo.orientations.forEach((orientationMarkerInfo) => {
        orientationMarkerInfo['azimuthLineMarker']?.verifyHighlightCriteria();
        orientationMarkerInfo.markers.forEach((cellMarkerInfo) => {
          cellMarkerInfo['cellTriangleMarker']?.verifyHighlightCriteria();
        });
      });
    });
  }, [highlightedCriterias, clickedCell]); //this useEffect' intend is to test highilighted criterias

  useEffect(() => {
    mapPopupStateRef.current = mapPopupState;
  }, [mapPopupState]);

  useEffect(() => {
    const zoomLevel = mapRef.current?.getZoom();
    const isAtLeastOneLayerSelected = reduxGlobalLayers.some(
      (el) => el.selected === true
    );
    const disabledLayersPresent = reduxGlobalLayers.some(
      (s) => s.minZoomLevel > zoomLevel
    );
    if (disabledLayersPresent) {
      topFiltersRef.current?.handleChangeLayersIconDetails({
        className: 'geoJson-layers-out-of-zoom',
        tootltipText: `Some layers dont support this zoom level`,
      });
    } else{
      if (isAtLeastOneLayerSelected) {
        topFiltersRef.current?.handleChangeLayersIconDetails({
          className: 'layer-selected',
          tootltipText: '',
        });
      } else {
        if (topFiltersRef.current?.layersIconDetails.className !== '') {
          topFiltersRef.current?.handleChangeLayersIconDetails({
            className: '',
            tootltipText: '',
          });
        }
      }
    }
  }, [reduxGlobalLayers])

  return (
    <>
      {dashboardId == 'ni-ran-advanced-map-dashboard' && filtersLoaded ? (
        <div className="mapMainContainer">
          {loading && (
            <Box class="loader-transparency-layer">
              <Loader />
            </Box>
          )}
          <MapContainer
            className="mapContainer"
            center={center} // Paris, France
            zoom={zoom}
            renderer={L.canvas()}
            maxZoom={18}
          >
            <MapEvents geoJsonLayersLoaded={geoJsonLayersLoaded} />
            <TileLayer
              url={
                tileLayerRef.current
                  ? ``
                  : mapRef.current && mapStyles.selected
                  ? getTileLayer(mapRef.current, selectedMapStyle.current)
                  : ''
              }
              attribution="&copy; OpenStreetMap contributors"
              opacity={0.3}
              renderer={L.canvas()}
            />
          </MapContainer>
          <Box className="filter-root-div">
            <TopFilters
              ref={topFiltersRef}
              bandsList={bandsList}
              dashboardId={dashboardId}
              initialFilters={filtersState}
              globalLayersControlRef={globalLayersControlRef}
              granularityDates={granularityDates}
              markersList={markersListAfterCellFilters}
              applySettingsClicked={applySettingsClicked}
              searchSuggestionsRef={searchSuggestionsRef}
              applyRegionSelection={applyRegionSelection}
              mapPopupStateVisibility={mapPopupState.isVisible}
              customGeoJsonLayers={customGeoJsonLayersData}
            />
            <Menu
              open={lineContextMenuOpened}
              anchorPosition={contextMenuPos}
              anchorReference="anchorPosition"
              className="filters-menu-container"
            >
              <Box className="map-layers-display">
                <MenuItem onClick={() => showEarthElevation()}>
                  Show earth elevation
                </MenuItem>
                <MenuItem onClick={() => showLineDistance()}>
                  Show line distance
                </MenuItem>
              </Box>
            </Menu>
          </Box>
          {cellElevationChart && (
            <ElevationChart
              points={chartData.points}
              closeElevation={closeElevation}
              linesData={chartData.linesData}
              hideTiltLine={chartData.hideTiltLine}
              distanceSteps={chartData.distanceSteps}
              earthElevationData={chartData.earthElevationData}
              actualTilt={clickedCell ? clickedCell['MECHANICAL TILT'] : 0}
            />
          )}
          <MarkerTable
            ref={markerTableRef}
            markers={selectedMarkers}
            cellDetailsOpen={clickedCell}
            kqiFiltersSelected={kqiFiltersSelected}
            kpiNRPropertyName={kpiNRPropertyRef.current}
            filteredMarkersWithKPI={kpiFiltered.markers}
            kpiLTEPropertyName={kpiLTEPropertyRef.current}
            handleCloseMarkersModal={handleCloseMarkersModal}
            mapPopupStateVisibility={mapPopupState.isVisible}
            handleKPILayerDataEvent={fetchTechnologiesKPIsData}
          />
          <TroubleTicketsTable
            ref={troubleTicketsTableRef}
            cellDetailsOpen={clickedCell}
            handleTroubleTicketsPopup={handleTroubleTicketsPopup}
          />
          {clickedCell && !mapPopupState.isVisible && (
            <Box className={'map-popup cell-details-popup'}>
              <CellDetails
                cell={clickedCell}
                recenterClickedCellSite={recenterClickedCellSite}
                detailsCmDump={detailsCmDump}
                siteStreetView={siteStreetView}
                kpiMarkers={kpiFiltered.markers}
                handleElementDetailsLayerClicked={
                  handleElementDetailsLayerClicked
                }
                previewButtonRef={previewButtonRef}
                displayHeightGraph={displayHeightGraph}
                kqiFiltersSelected={kqiFiltersSelected}
                selectedKpiValues={kpiFiltered.selectedValues}
                troubleTicketsTableRef={troubleTicketsTableRef}
                handleCloseCellDetails={handleCloseCellDetails}
                hideElementDetailLayer={hideElementDetailLayer}
                detailsPerformanceDetails={detailsPerformanceDetails}
                activeElementDetailsLayers={activeElementDetailsLayers}
              />
            </Box>
          )}
          <Layers
            L={L}
            mapRef={mapRef}
            ref={globalLayersControlRef}
            tileLayerRef={tileLayerRef}
            topFiltersRef={topFiltersRef}
            markerTableRef={markerTableRef}
            createTileLayer={createTileLayer}
            selectedMapStyle={selectedMapStyle}
            handleLayersDataEvents={handleLayersDataEvents}
            onUploadGeoJsonLayer={handleUploadGeoJsonLayer}
            onToggleCustomGeoJsonLayer={handleToggleCustomGeoJsonLayer}
            onRemoveCustomGeoJsonLayer={handleRemoveCustomGeoJsonLayer}
          />
          <SearchSuggestions
            ref={searchSuggestionsRef}
            handleSuggestionSelect={handleSuggestionSelect}
            searchInputRef={topFiltersRef.current?.searchInputRef}
          />
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default MapComponent;
